import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const GOLD = '#D4AF37';
const BG_CARD = '#0d1117';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';
const GREEN = '#10b981';
const INPUT_BG = '#0a0f1e';

const cardStyle = { background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24, marginBottom: 16 };

export default function TaxSummary() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [servicePrice, setServicePrice] = useState(35);

  useEffect(() => { load(); }, [year]);

  function load() {
    setLoading(true);
    api.get(`/analytics/tax-summary?year=${year}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const setAsidePerAppt = data ? (servicePrice * (data.setAsidePercent / 100)) : 0;

  const fmt = (n) => {
    if (!n) return '$0';
    return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtPct = (n) => `${Math.round(n || 0)}%`;

  return (
    <AdminLayout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>Tax Summary</h1>
            <p style={{ color: TEXT_SEC, fontSize: 14, margin: '4px 0 0' }}>Estimated tax obligations for your business</p>
          </div>
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            style={{ background: INPUT_BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 16px', color: '#fff', fontSize: 14, outline: 'none', cursor: 'pointer' }}
          >
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Florida no state income tax callout */}
        <div style={{ background: '#10b98115', border: '1px solid #10b98133', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, color: GREEN, fontWeight: 700 }}>FL</span>
          <div>
            <div style={{ color: GREEN, fontWeight: 700, fontSize: 15 }}>Florida has no state income tax ✓</div>
            <div style={{ color: TEXT_SEC, fontSize: 13 }}>You only owe federal SE tax + federal income tax. Big savings.</div>
          </div>
        </div>

        {loading ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
            <div style={{ color: TEXT_SEC }}>Loading...</div>
          </div>
        ) : data ? (
          <>
            {/* Main stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total Revenue', value: fmt(data.totalRevenue), sub: `${year} YTD`, color: '#fff' },
                { label: 'Self-Employment Tax (15.3%)', value: fmt(data.seTax), sub: 'On 92.35% of net profit', color: '#f59e0b' },
                { label: 'Estimated Federal Tax', value: fmt(data.federalTax), sub: 'Based on income brackets', color: '#f59e0b' },
                { label: 'Total to Set Aside', value: fmt(data.totalTax), sub: `~${fmtPct(data.setAsidePercent)} of revenue`, color: '#ef4444' },
              ].map(stat => (
                <div key={stat.label} style={{ ...cardStyle, marginBottom: 0 }}>
                  <div style={{ color: stat.color, fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{stat.value}</div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{stat.label}</div>
                  <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 2 }}>{stat.sub}</div>
                </div>
              ))}
            </div>

            {/* Set aside progress bar */}
            <div style={{ ...cardStyle }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: '#fff', fontWeight: 700 }}>Set-Aside Rate</span>
                <span style={{ color: GOLD, fontWeight: 800 }}>{fmtPct(data.setAsidePercent)}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 999, height: 10, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(data.setAsidePercent, 100)}%`, height: '100%', background: `linear-gradient(to right, ${GOLD}, #f59e0b)`, borderRadius: 999, transition: 'width 0.5s' }} />
              </div>
              <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 10 }}>
                For every $100 earned, set aside approximately <strong style={{ color: GOLD }}>${data.setAsidePercent}</strong> for taxes.
              </div>
            </div>

            {/* Quarterly breakdown */}
            <div style={{ ...cardStyle }}>
              <h3 style={{ color: '#fff', margin: '0 0 20px', fontSize: 16 }}>Quarterly Breakdown</h3>
              <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr', gap: 8 }}>
                <span style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Quarter</span>
                <span style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Revenue</span>
                <span style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Est. Tax</span>
                <span style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Quarterly Payment</span>
              </div>
              {data.quarters.map((q, i) => {
                const qTax = (q.total * data.setAsidePercent) / 100;
                return (
                  <div key={q.label} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr', gap: 8, padding: '12px 0', borderTop: i > 0 ? `1px solid ${BORDER}` : 'none', alignItems: 'center' }}>
                    <div>
                      <span style={{ color: '#fff', fontWeight: 700 }}>{q.label}</span>
                      <span style={{ color: TEXT_SEC, fontSize: 11, marginLeft: 8 }}>{q.start.slice(5).replace('-', '/')} – {q.end.slice(5).replace('-', '/')}</span>
                    </div>
                    <span style={{ color: q.total > 0 ? '#fff' : TEXT_SEC, fontWeight: 600 }}>{fmt(q.total)}</span>
                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>{fmt(qTax)}</span>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>{fmt(qTax)}</span>
                  </div>
                );
              })}
              <div style={{ marginTop: 16, padding: '12px 16px', background: GOLD + '11', border: `1px solid ${GOLD}22`, borderRadius: 10 }}>
                <div style={{ color: GOLD, fontWeight: 700, fontSize: 14 }}>Suggested quarterly tax payment: {fmt(data.quarterlyPayment)}</div>
                <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 2 }}>Due: Apr 15, Jun 15, Sep 15, Jan 15</div>
              </div>
            </div>

            {/* Set aside per appointment calculator */}
            <div style={{ ...cardStyle }}>
              <h3 style={{ color: '#fff', margin: '0 0 16px', fontSize: 16 }}>Set Aside Per Appointment</h3>
              <p style={{ color: TEXT_SEC, fontSize: 13, margin: '0 0 16px' }}>How much to set aside from each service:</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: TEXT_SEC }}>$</span>
                  <input
                    type="number"
                    min="1"
                    value={servicePrice}
                    onChange={e => setServicePrice(parseFloat(e.target.value) || 0)}
                    style={{ width: 80, background: INPUT_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 14, outline: 'none' }}
                  />
                  <span style={{ color: TEXT_SEC }}>service</span>
                </div>
                <span style={{ color: TEXT_SEC }}>→</span>
                <div style={{ background: GOLD + '22', border: `1px solid ${GOLD}44`, borderRadius: 10, padding: '10px 20px' }}>
                  <span style={{ color: GOLD, fontWeight: 800, fontSize: 20 }}>${setAsidePerAppt.toFixed(2)}</span>
                  <span style={{ color: TEXT_SEC, fontSize: 13, marginLeft: 6 }}>set aside</span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 18px', color: TEXT_SEC, fontSize: 12 }}>
              <strong>Disclaimer:</strong> This is an estimate based on 2024 federal tax brackets for a single filer. Actual tax obligations may vary. Consult a qualified tax professional for personalized advice.
            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
